import React, { useEffect, useRef, useState } from "react";
import { ArrowUp, ArrowDown } from "lucide-react";
import { ABtn, AScore, AResult, PadButton } from "./arcadeUi";
import { sfx } from "@/lib/soundfx";

const W = 360, H = 260, PH = 54, PW = 10, R = 6, WIN = 5;

export default function Pong() {
  const canvasRef = useRef(null);
  const g = useRef({});
  const move = useRef(0);
  const [p, setP] = useState(0);
  const [c, setC] = useState(0);
  const [status, setStatus] = useState("idle");

  const serve = (dir) => { const s = g.current; s.bx = W / 2; s.by = H / 2; s.vx = dir * 4; s.vy = (Math.random() * 4 - 2); };
  const start = () => { g.current = { py: H / 2 - PH / 2, cy: H / 2 - PH / 2 }; serve(Math.random() > 0.5 ? 1 : -1); setP(0); setC(0); setStatus("playing"); sfx.beep(); };

  useEffect(() => {
    const kd = (e) => { if (["ArrowUp", "w"].includes(e.key)) { move.current = -1; e.preventDefault(); } else if (["ArrowDown", "s"].includes(e.key)) { move.current = 1; e.preventDefault(); } };
    const ku = (e) => { if (["ArrowUp", "w", "ArrowDown", "s"].includes(e.key)) move.current = 0; };
    window.addEventListener("keydown", kd); window.addEventListener("keyup", ku);
    return () => { window.removeEventListener("keydown", kd); window.removeEventListener("keyup", ku); };
  }, []);

  useEffect(() => {
    const cvs = canvasRef.current, ctx = cvs.getContext("2d");
    const drag = (e) => { if (status !== "playing") return; const r = cvs.getBoundingClientRect(); const y = (e.clientY - r.top) / r.height * H; g.current.py = Math.max(0, Math.min(H - PH, y - PH / 2)); };
    cvs.addEventListener("pointermove", drag);
    const draw = () => {
      ctx.fillStyle = "#0a0a18"; ctx.fillRect(0, 0, W, H);
      const s = g.current;
      ctx.strokeStyle = "#334"; ctx.setLineDash([6, 8]); ctx.beginPath(); ctx.moveTo(W / 2, 0); ctx.lineTo(W / 2, H); ctx.stroke(); ctx.setLineDash([]);
      ctx.fillStyle = "#a7f3d0"; ctx.fillRect(8, s.py || 0, PW, PH);
      ctx.fillStyle = "#f77737"; ctx.fillRect(W - 8 - PW, s.cy || 0, PW, PH);
      ctx.fillStyle = "#fff"; ctx.fillRect((s.bx || W / 2) - R, (s.by || H / 2) - R, R * 2, R * 2);
    };
    if (status !== "playing") { draw(); return; }
    let raf, pR = 0, cR = 0;
    const loop = () => {
      raf = requestAnimationFrame(loop);
      const s = g.current;
      s.py = Math.max(0, Math.min(H - PH, s.py + move.current * 6));
      const target = s.by - PH / 2; s.cy = Math.max(0, Math.min(H - PH, s.cy + Math.max(-4.2, Math.min(4.2, (target - s.cy) * 0.12))));
      s.bx += s.vx; s.by += s.vy;
      if (s.by < R) { s.by = R; s.vy *= -1; } if (s.by > H - R) { s.by = H - R; s.vy *= -1; }
      if (s.bx - R < 18 && s.by > s.py && s.by < s.py + PH && s.vx < 0) { s.vx = Math.abs(s.vx) * 1.04; s.vy += ((s.by - (s.py + PH / 2)) / (PH / 2)) * 2; s.bx = 18 + R; sfx.click(); }
      if (s.bx + R > W - 18 && s.by > s.cy && s.by < s.cy + PH && s.vx > 0) { s.vx = -Math.abs(s.vx); s.vy += ((s.by - (s.cy + PH / 2)) / (PH / 2)) * 2; s.bx = W - 18 - R; sfx.click(); }
      if (s.bx < 0) cR = 1;
      if (s.bx > W) pR = 1;
      if (pR || cR) {
        if (pR) setP((v) => { const nv = v + 1; if (nv >= WIN) { cancelAnimationFrame(raf); sfx.win(); setStatus("win"); } return nv; });
        else setC((v) => { const nv = v + 1; if (nv >= WIN) { cancelAnimationFrame(raf); sfx.explode(); setStatus("over"); } return nv; });
        serve(pR ? -1 : 1); pR = 0; cR = 0;
      }
      draw();
    };
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); cvs.removeEventListener("pointermove", drag); };
  }, [status]);

  return (
    <div className="flex flex-col items-center">
      <div className="mb-2 flex gap-2"><AScore label="You" value={p} /><AScore label="CPU" value={c} /></div>
      <div className="relative">
        <canvas ref={canvasRef} width={W} height={H} className="w-full max-w-[360px] rounded-md border-[3px] border-kirana-brown" style={{ imageRendering: "pixelated", touchAction: "none" }} />
        {status !== "playing" && (
          <div className="absolute inset-0 flex flex-col items-center justify-center rounded-md bg-black/60 p-3">
            {status !== "idle" && <AResult win={status === "win"} text={status === "win" ? "You Win! 🏓" : "CPU Wins"} testId="pong-over" />}
            <ABtn tone="green" onClick={start} className="mt-3">{status === "idle" ? "Start" : "Play Again"}</ABtn>
          </div>
        )}
      </div>
      <p className="mt-2 text-center text-xs text-kirana-brown/60">First to {WIN} · drag or use buttons</p>
      <div className="mt-1 flex gap-4">
        <PadButton testId="pong-up" onPress={() => { move.current = -1; }} onRelease={() => { move.current = 0; }}><ArrowUp className="h-5 w-5" /></PadButton>
        <PadButton testId="pong-down" onPress={() => { move.current = 1; }} onRelease={() => { move.current = 0; }}><ArrowDown className="h-5 w-5" /></PadButton>
      </div>
    </div>
  );
}
